import { USE_MOCKS, api, getActiveEntityId, mockDelay } from "@/lib/api"
import type {
  BookId,
  ClassificationSource,
  JournalEntry,
  LedgerAccount,
  LedgerMovement,
  TrialBalanceRow,
} from "@/lib/types"
import type {
  AccountOption,
  AccountRow,
  AdjustmentEntryData,
  EntryCreatedRow,
  EntryDetailRow,
  JournalEntryRow,
  JournalEntryView,
  LedgerAccountRow,
  TrialBalanceApiRow,
  TrialBalanceResult,
} from "@/routes/books/mapping"
import {
  accountFromApi,
  adjustmentEntryToApi,
  bookToApi,
  entryCreatedFromApi,
  entryFromApi,
  journalEntryFromApi,
  ledgerAccountFromApi,
  trialBalanceFromApi,
} from "@/routes/books/mapping"

/**
 * Vista contable: diario, mayor, balance de comprobacion y analisis de cuenta.
 * Modulo `ledger` del backend.
 *
 *   GET  /api/ledger/journal            ?entity_id&libro&desde&hasta&limite
 *   GET  /api/ledger/accounts           ?pais&con_movimiento
 *   GET  /api/ledger/ledger/{cuenta}    ?entity_id&libro&desde&hasta
 *   GET  /api/ledger/trial-balance      ?entity_id&libro&hasta   <- `hasta` obligatoria
 *   GET  /api/ledger/entries/{id}       (el drill-down: unico que trae lineas)
 *   POST /api/ledger/entries            { entity_id, libro, fecha, glosa, lineas[], motivo }
 *
 * Este archivo NO traduce: decide ruta y verbo y delega la forma del cuerpo y de
 * la respuesta a `mapping.ts`. `USE_MOCKS` sigue intacto y devuelve los tipos de
 * la SPA sin pasar por el mapeo.
 *
 * `entity_id` es OBLIGATORIO en cuatro de las cinco lecturas: un libro pertenece
 * a una sociedad, no al tenant. Como la SPA todavia no tiene selector de entidad,
 * `entidadActiva()` la resuelve una vez por sesion (ver su docstring).
 */

/**
 * La sociedad sobre la que se leen los libros.
 *
 * Cuatro de las cinco lecturas de `ledger` la exigen: un libro pertenece a una
 * SOCIEDAD, no al tenant. La eligio la persona en el selector de la cabecera y
 * vive en la sesion (`lib/activeEntity.ts` -> `lib/api.ts`); aqui solo se lee.
 *
 * Si no hay ninguna elegida, esto LANZA en vez de mandar la peticion. Es
 * deliberado y son dos razones distintas:
 *
 *  - el 422 del backend nombraria `entity_id`, un campo que el usuario no puede
 *    rellenar desde ninguna parte de la pantalla;
 *  - y el mensaje llega en el idioma del servidor, no en el del problema.
 *
 * `DataState` muestra este texto tal cual, que dice que falta y donde se
 * arregla. Es el mismo criterio que `lib/entities.ts` documenta para los
 * listados de maestros.
 */
function entidadActiva(): string {
  const entityId = getActiveEntityId()
  if (!entityId) {
    throw new Error(
      "Elige una sociedad en la barra superior. Los libros pertenecen a una " +
        "sociedad, no a la empresa: sin elegirla no hay diario, mayor ni balance " +
        "que mostrar.",
    )
  }
  return entityId
}

/**
 * Denominaciones de cuenta usadas por los MOCKS y como respaldo de los
 * desplegables mientras el catalogo real carga.
 *
 * OJO: estos codigos de 5 digitos (subdivisionarias) NO existen en
 * `account_local` — el plan sembrado llega a la divisionaria de 3 ("639"). Con
 * ellos, todo asiento manual contra el backend real moria con "cuenta
 * desconocida". Las pantallas usan ahora `fetchAccounts()`.
 */
export const ACCOUNT_NAMES: Record<string, string> = {
  "1011": "Caja y bancos - cuentas corrientes",
  "12121": "Facturas por cobrar - terceros",
  "33611": "Equipos de computo",
  "40111": "IGV - cuenta propia",
  "40171": "Administradoras de fondos de pensiones",
  "41111": "Remuneraciones por pagar",
  "42121": "Facturas por pagar - terceros",
  "5011": "Capital social",
  "62111": "Sueldos y salarios",
  "62711": "Seguro de salud (EsSalud)",
  "63111": "Transporte de carga",
  "63241": "Asesoria legal",
  "63271": "Asesoria empresarial",
  "63611": "Energia electrica",
  "63711": "Gastos de representacion y publicidad",
  "63911": "Otros servicios prestados por terceros",
  "65911": "Sanciones administrativas",
  "70121": "Ventas - servicios",
  "79111": "Cargas imputables a cuentas de costos y gastos",
  "92111": "Costo del servicio",
  "94111": "Gastos de administracion",
  "95111": "Gastos de ventas",
  "88111": "Adiciones tributarias",
  "88911": "Contrapartida ajustes tributarios",
  "99911": "Contrapartida ajustes gerenciales",
}

/** Saldos de apertura del periodo (statutory). Cuadran entre si. */
const OPENING_BALANCES: Record<string, number> = {
  "1011": 120000,
  "5011": -120000,
}

function line(
  id: string,
  accountCode: string,
  debit: number,
  credit: number,
  costCenter?: string,
  memo?: string,
) {
  return {
    id,
    accountCode,
    accountName: ACCOUNT_NAMES[accountCode] ?? accountCode,
    debit,
    credit,
    costCenter,
    memo,
  }
}

const MOCK_ENTRIES: JournalEntry[] = [
  {
    id: "je-001",
    number: "07-000001",
    date: "2026-07-03",
    book: "STATUTORY_PE",
    glosa: "Servicios cloud julio - Servicios Cloud Andina SAC",
    documentRef: "F001-0004821",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-03T09:12:00Z",
    status: "posteado",
    lines: [
      line("l1", "63911", 4200, 0, "CC-TEC"),
      line("l2", "40111", 756, 0),
      line("l3", "42121", 0, 4956),
    ],
  },
  {
    id: "je-002",
    number: "07-000002",
    date: "2026-07-04",
    book: "STATUTORY_PE",
    glosa: "Transporte de carga - Transportes Vega SAC (detraccion 4%)",
    documentRef: "F002-0000912",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-04T10:02:00Z",
    status: "posteado",
    lines: [
      line("l1", "63111", 8500, 0, "CC-OPE"),
      line("l2", "40111", 1530, 0),
      line("l3", "42121", 0, 10030),
    ],
  },
  {
    id: "je-003",
    number: "07-000003",
    date: "2026-07-05",
    book: "STATUTORY_PE",
    glosa: "Asesoria legal - Estudio Paredes (USD 3,000 t.c. 3.750)",
    documentRef: "E001-0000233",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-05T15:40:00Z",
    status: "posteado",
    lines: [
      line("l1", "63241", 11250, 0, "CC-ADM"),
      line("l2", "40111", 2025, 0),
      line("l3", "42121", 0, 13275),
    ],
  },
  {
    id: "je-004",
    number: "07-000004",
    date: "2026-07-07",
    book: "STATUTORY_PE",
    glosa: "Atencion a cliente - Restaurante La Bahia EIRL",
    documentRef: "F100-0001177",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-07T18:20:00Z",
    status: "posteado",
    lines: [
      line("l1", "63711", 980, 0, "CC-COM", "Gasto de representacion sujeto a limite"),
      line("l2", "40111", 176.4, 0),
      line("l3", "42121", 0, 1156.4),
    ],
  },
  {
    id: "je-005",
    number: "07-000005",
    date: "2026-07-08",
    book: "STATUTORY_PE",
    glosa: "Multa municipal - Municipalidad de San Isidro",
    documentRef: "F001-0009011",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-08T11:05:00Z",
    status: "posteado",
    lines: [line("l1", "65911", 640, 0, "CC-ADM"), line("l2", "42121", 0, 640)],
  },
  {
    id: "je-006",
    number: "07-000006",
    date: "2026-07-12",
    book: "STATUTORY_PE",
    glosa: "Consultoria - Talento Plus SAC (detraccion 12%)",
    documentRef: "F003-0000781",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-12T09:00:00Z",
    status: "posteado",
    lines: [
      line("l1", "63271", 15000, 0, "CC-OPE"),
      line("l2", "40111", 2700, 0),
      line("l3", "42121", 0, 17700),
    ],
  },
  {
    id: "je-007",
    number: "07-000007",
    date: "2026-07-15",
    book: "STATUTORY_PE",
    glosa: "Compra de 8 laptops - Equipos Tecnologicos del Sur SAC",
    documentRef: "F001-0000158",
    origin: "manual",
    createdBy: "Rosa Quispe",
    createdAt: "2026-07-15T16:30:00Z",
    reason: "Se capitaliza por superar el umbral de la politica de activos fijos.",
    status: "posteado",
    lines: [
      line("l1", "33611", 12400, 0, "CC-TEC"),
      line("l2", "40111", 2232, 0),
      line("l3", "42121", 0, 14632),
    ],
  },
  {
    id: "je-008",
    number: "07-000008",
    date: "2026-07-18",
    book: "STATUTORY_PE",
    glosa: "Energia electrica julio - Energia del Peru SA",
    documentRef: "F011-0003390",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-18T08:15:00Z",
    status: "posteado",
    lines: [
      line("l1", "63611", 1830, 0, "CC-ADM"),
      line("l2", "40111", 329.4, 0),
      line("l3", "42121", 0, 2159.4),
    ],
  },
  {
    id: "je-009",
    number: "07-000009",
    date: "2026-07-25",
    book: "STATUTORY_PE",
    glosa: "Facturacion de servicios del mes",
    documentRef: "Registro de ventas 14.1",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-25T20:00:00Z",
    status: "posteado",
    lines: [
      line("l1", "12121", 141600, 0),
      line("l2", "70121", 0, 120000),
      line("l3", "40111", 0, 21600),
    ],
  },
  {
    id: "je-010",
    number: "07-000010",
    date: "2026-07-31",
    book: "STATUTORY_PE",
    glosa: "Provision de planilla julio",
    documentRef: "PLAME 07-2026",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-31T22:10:00Z",
    status: "posteado",
    lines: [
      line("l1", "62111", 38000, 0),
      line("l2", "62711", 3420, 0, undefined, "EsSalud 9%"),
      line("l3", "40171", 0, 4940, undefined, "AFP"),
      line("l4", "41111", 0, 36480),
    ],
  },
  {
    id: "je-011",
    number: "07-000011",
    date: "2026-07-31",
    book: "STATUTORY_PE",
    glosa: "Destino de gastos por funcion (clase 9)",
    origin: "cierre",
    createdBy: "motor",
    createdAt: "2026-07-31T22:20:00Z",
    status: "posteado",
    lines: [
      line("l1", "92111", 40000, 0, "CC-OPE"),
      line("l2", "94111", 30000, 0, "CC-ADM"),
      line("l3", "95111", 13820, 0, "CC-COM"),
      line("l4", "79111", 0, 83820),
    ],
  },
  {
    id: "je-012",
    number: "07-000012",
    date: "2026-07-31",
    book: "STATUTORY_PE",
    glosa: "Pago a proveedores - lote quincenal",
    documentRef: "Lote BCP 07-02",
    origin: "automatico",
    createdBy: "motor",
    createdAt: "2026-07-31T23:00:00Z",
    status: "posteado",
    lines: [line("l1", "42121", 30000, 0), line("l2", "1011", 0, 30000)],
  },
  {
    id: "je-013",
    number: "TX-07-0001",
    date: "2026-07-31",
    book: "TAX",
    glosa: "Adicion por exceso de gastos de representacion (art. 37 inc. q LIR)",
    origin: "ajuste",
    createdBy: "Rosa Quispe",
    createdAt: "2026-07-31T23:30:00Z",
    reason: "Excede el limite de 0.5% de ingresos brutos acumulados al 31/07.",
    status: "posteado",
    lines: [line("l1", "88111", 380, 0), line("l2", "88911", 0, 380)],
  },
  {
    id: "je-014",
    number: "MG-07-0001",
    date: "2026-07-31",
    book: "MANAGEMENT",
    glosa: "Exclusion de multas del P&L gerencial (regla TR-014)",
    origin: "ajuste",
    createdBy: "motor",
    createdAt: "2026-07-31T23:35:00Z",
    reason: "Regla de tratamiento TR-014: la multa no es operativa.",
    status: "posteado",
    lines: [line("l1", "99911", 640, 0), line("l2", "65911", 0, 640, "CC-ADM")],
  },
  {
    id: "je-015",
    number: "MG-07-0002",
    date: "2026-07-31",
    book: "MANAGEMENT",
    glosa: "Normalizacion de sueldo del socio a valor de mercado",
    origin: "ajuste",
    createdBy: "Rosa Quispe",
    createdAt: "2026-07-31T23:40:00Z",
    reason: "Regla TR-021: el sueldo pagado esta bajo mercado y distorsiona el margen.",
    status: "posteado",
    lines: [line("l1", "62111", 6000, 0, "CC-ADM"), line("l2", "99911", 0, 6000)],
  },
]

/** Origen de la clasificacion por asiento, para el drill-down. */
const ENTRY_SOURCE: Record<string, ClassificationSource> = {
  "je-001": "llm",
  "je-002": "regla",
  "je-003": "memoria",
  "je-004": "llm",
  "je-005": "regla",
  "je-006": "llm",
  "je-007": "humano",
  "je-008": "memoria",
  "je-009": "regla",
  "je-010": "regla",
  "je-011": "regla",
  "je-012": "regla",
  "je-013": "humano",
  "je-014": "regla",
  "je-015": "humano",
}

export interface JournalFilters {
  book: BookId
  from?: string
  to?: string
  accountCode?: string
  origin?: string
  query?: string
}

/**
 * El diario.
 *
 * Devuelve `JournalEntryView` y no `JournalEntry` porque el listado del backend
 * NO trae las lineas (hace `GROUP BY je.id`): trae los totales ya sumados. La
 * pantalla usa esos totales para la cabecera; el detalle con lineas se pide por
 * asiento con `fetchEntry`. Ver la nota larga en `mapping.ts`.
 */
export async function fetchJournal(filters: JournalFilters): Promise<JournalEntryView[]> {
  if (USE_MOCKS) {
    const entries = MOCK_ENTRIES.filter((entry) => {
      if (entry.book !== filters.book) return false
      if (filters.from && entry.date < filters.from) return false
      if (filters.to && entry.date > filters.to) return false
      if (filters.origin && filters.origin !== "todos" && entry.origin !== filters.origin)
        return false
      if (filters.accountCode && filters.accountCode !== "todas") {
        if (!entry.lines.some((l) => l.accountCode === filters.accountCode)) return false
      }
      if (filters.query) {
        const q = filters.query.toLowerCase()
        const haystack = `${entry.number} ${entry.glosa} ${entry.documentRef ?? ""} ${entry.createdBy}`.toLowerCase()
        if (!haystack.includes(q)) return false
      }
      return true
    })
    return mockDelay(entries.map(vistaDesdeMock))
  }

  const entityId = entidadActiva()
  const rows = await api.get<JournalEntryRow[]>("/ledger/journal", {
    query: {
      entity_id: entityId,
      libro: bookToApi(filters.book),
      desde: filters.from,
      hasta: filters.to,
      limite: 200,
    },
  })

  // `accountCode`, `origin` y `query` no existen como parametros del endpoint
  // (declara entity_id, libro, desde, hasta y limite). Se filtra en el cliente
  // sobre lo que devolvio: mandarlos como query param seria peor que no
  // filtrar, porque FastAPI ignora lo que no declara y la pantalla diria
  // "filtrado" mostrando la lista completa.
  // El filtro por CUENTA no se puede aplicar aqui: el listado no trae lineas.
  // TODO(backend): `origen`, `q` y `cuenta` en `/ledger/journal`.
  return rows
    .map((row) => journalEntryFromApi(row, { book: filters.book }))
    .filter((entry) => {
      if (filters.origin && filters.origin !== "todos" && entry.origin !== filters.origin) {
        return false
      }
      if (filters.query) {
        const q = filters.query.toLowerCase()
        const heno = `${entry.number} ${entry.glosa} ${entry.documentRef ?? ""}`.toLowerCase()
        if (!heno.includes(q)) return false
      }
      return true
    })
}

/** Los mocks traen lineas de verdad: los totales de la vista salen de sumarlas. */
function vistaDesdeMock(entry: JournalEntry): JournalEntryView {
  return {
    ...entry,
    totalDebit: entry.lines.reduce((sum, l) => sum + l.debit, 0),
    totalCredit: entry.lines.reduce((sum, l) => sum + l.credit, 0),
    lineCount: entry.lines.length,
    linesLoaded: true,
  }
}

/**
 * El plan de cuentas local. Es la lista que alimenta TODOS los desplegables de
 * cuenta de la SPA (mayor, analisis, asiento de ajuste y correccion de
 * clasificacion), y por eso no puede seguir siendo una constante: los codigos
 * fijos de `ACCOUNT_NAMES` no existen en `account_local` y con ellos toda
 * escritura moria en 422.
 */
export async function fetchAccounts(): Promise<AccountOption[]> {
  if (USE_MOCKS) {
    return mockDelay(
      Object.entries(ACCOUNT_NAMES)
        .map(([code, name]) => ({ code, name }))
        .sort((a, b) => a.code.localeCompare(b.code)),
      80,
    )
  }
  const rows = await api.get<AccountRow[]>("/ledger/accounts")
  return rows.map(accountFromApi)
}

export async function fetchLedgerAccount(params: {
  accountCode: string
  book: BookId
  from?: string
  to?: string
  /** El backend devuelve el codigo pero no el nombre: lo pone la pantalla. */
  accountName?: string
}): Promise<LedgerAccount> {
  if (USE_MOCKS) {
    const opening = OPENING_BALANCES[params.accountCode] ?? 0
    let running = opening
    const movements: LedgerMovement[] = []

    MOCK_ENTRIES.filter((entry) => entry.book === params.book)
      .filter((entry) => (params.from ? entry.date >= params.from : true))
      .filter((entry) => (params.to ? entry.date <= params.to : true))
      .sort((a, b) => a.date.localeCompare(b.date) || a.number.localeCompare(b.number))
      .forEach((entry) => {
        entry.lines
          .filter((l) => l.accountCode === params.accountCode)
          .forEach((l) => {
            running += l.debit - l.credit
            movements.push({
              id: `${entry.id}-${l.id}`,
              date: entry.date,
              entryNumber: entry.number,
              entryId: entry.id,
              glosa: l.memo ? `${entry.glosa} · ${l.memo}` : entry.glosa,
              documentRef: entry.documentRef,
              debit: l.debit,
              credit: l.credit,
              balance: running,
            })
          })
      })

    return mockDelay({
      accountCode: params.accountCode,
      accountName: ACCOUNT_NAMES[params.accountCode] ?? params.accountCode,
      openingBalance: opening,
      movements,
      closingBalance: running,
    })
  }

  const entityId = entidadActiva()
  const row = await api.get<LedgerAccountRow>(
    `/ledger/ledger/${encodeURIComponent(params.accountCode)}`,
    {
      query: {
        entity_id: entityId,
        libro: bookToApi(params.book),
        desde: params.from,
        hasta: params.to,
      },
    },
  )
  return ledgerAccountFromApi(row, { accountName: params.accountName })
}

export type { TrialBalanceResult } from "@/routes/books/mapping"

export async function fetchTrialBalance(params: {
  book: BookId
  cutoff: string
}): Promise<TrialBalanceResult> {
  if (USE_MOCKS) {
    const accumulator = new Map<string, { debit: number; credit: number }>()

    MOCK_ENTRIES.filter((entry) => entry.book === params.book)
      .filter((entry) => entry.date <= params.cutoff)
      .forEach((entry) => {
        entry.lines.forEach((l) => {
          const current = accumulator.get(l.accountCode) ?? { debit: 0, credit: 0 }
          current.debit += l.debit
          current.credit += l.credit
          accumulator.set(l.accountCode, current)
        })
      })

    if (params.book === "STATUTORY_PE") {
      Object.keys(OPENING_BALANCES).forEach((code) => {
        if (!accumulator.has(code)) accumulator.set(code, { debit: 0, credit: 0 })
      })
    }

    const rows: TrialBalanceRow[] = Array.from(accumulator.entries())
      .map(([code, sums]) => {
        const opening = params.book === "STATUTORY_PE" ? OPENING_BALANCES[code] ?? 0 : 0
        const closing = opening + sums.debit - sums.credit
        return {
          accountCode: code,
          accountName: ACCOUNT_NAMES[code] ?? code,
          level: 1,
          openingDebit: opening > 0 ? opening : 0,
          openingCredit: opening < 0 ? -opening : 0,
          periodDebit: sums.debit,
          periodCredit: sums.credit,
          closingDebit: closing > 0 ? closing : 0,
          closingCredit: closing < 0 ? -closing : 0,
        }
      })
      .sort((a, b) => a.accountCode.localeCompare(b.accountCode))

    const totals = rows.reduce(
      (acc, row) => ({
        openingDebit: acc.openingDebit + row.openingDebit,
        openingCredit: acc.openingCredit + row.openingCredit,
        periodDebit: acc.periodDebit + row.periodDebit,
        periodCredit: acc.periodCredit + row.periodCredit,
        closingDebit: acc.closingDebit + row.closingDebit,
        closingCredit: acc.closingCredit + row.closingCredit,
      }),
      {
        openingDebit: 0,
        openingCredit: 0,
        periodDebit: 0,
        periodCredit: 0,
        closingDebit: 0,
        closingCredit: 0,
      },
    )

    const balanced =
      Math.abs(totals.periodDebit - totals.periodCredit) < 0.005 &&
      Math.abs(totals.closingDebit - totals.closingCredit) < 0.005

    return mockDelay({ rows, totals, balanced })
  }

  // `hasta` es obligatoria en el backend: un balance de comprobacion sin fecha
  // de corte no significa nada. Es la misma fecha "Hasta / corte" de la barra.
  const entityId = entidadActiva()
  const row = await api.get<TrialBalanceApiRow>("/ledger/trial-balance", {
    query: { entity_id: entityId, libro: bookToApi(params.book), hasta: params.cutoff },
  })
  return trialBalanceFromApi(row)
}

export async function fetchEntry(entryId: string): Promise<JournalEntry> {
  if (USE_MOCKS) {
    const entry = MOCK_ENTRIES.find((e) => e.id === entryId)
    if (!entry) throw new Error("Asiento no encontrado")
    return mockDelay(entry, 60)
  }
  const row = await api.get<EntryDetailRow>(`/ledger/entries/${entryId}`)
  return entryFromApi(row)
}

/** La forma que produce `AdjustmentEntryDialog`; la traduce `mapping.ts`. */
export type AdjustmentEntryPayload = AdjustmentEntryData

export async function createAdjustmentEntry(payload: AdjustmentEntryPayload) {
  if (USE_MOCKS) return mockDelay({ id: "je-nuevo", book: payload.book, total: 0 })
  const entityId = entidadActiva()
  const row = await api.post<EntryCreatedRow>(
    "/ledger/entries",
    adjustmentEntryToApi(payload, entityId),
  )
  return entryCreatedFromApi(row)
}

/* ------------------------------------------------- comparacion de libros --- */

/** `GET /ledger/compare-books` */
export interface CompareBooksRow {
  periodo: string
  por_libro: Record<string, { ingresos: number; gastos: number; resultado: number }>
  diferencias_explicadas: Array<{
    nombre: string
    motivo: string | null
    libro: string
    asientos: number
    efecto: number
  }>
  nota: string
}

/**
 * Por que el resultado contable y el gerencial no son el mismo numero.
 *
 * Es la pregunta que define el producto entero, y hasta ahora el endpoint que
 * la respondia no tenia pantalla: solo se podia ver con curl.
 *
 * `periodo` es AAAA-MM. El backend agrupa por `journal_entry.periodo`, que es
 * una cadena de mes, no un rango de fechas: no hay forma de pedir "del 12 al
 * 27". Por eso esta vista usa el mes de la fecha de corte de la barra y lo dice,
 * en vez de fingir que respeta un rango que el backend no puede honrar.
 */
export async function fetchCompareBooks(periodo: string): Promise<CompareBooksRow> {
  if (USE_MOCKS) {
    return mockDelay<CompareBooksRow>({
      periodo, por_libro: {}, diferencias_explicadas: [], nota: "",
    })
  }
  const entityId = entidadActiva()
  return api.get<CompareBooksRow>("/ledger/compare-books", {
    query: { entity_id: entityId, periodo },
  })
}
